import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { domainService, domainTypeService, userGroupService } from '../services/api';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Alert,
  Snackbar,
  Divider,
  Chip,
  Avatar,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Domain as DomainIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Group as GroupIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as AttachMoneyIcon,
  Comment as CommentIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout/Layout';

const DomainForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    name: '',
    domain_name: '',
    domain_type: '',
    registration_date: '',
    expiration_date: '',
    renewal_value: '',
    is_active: true,
    comments: '',
    status: 'active',
    location: '',
    technician_in_charge: '',
    group_in_charge: '',
    permission_group_ids: []
  });

  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [isEdit, setIsEdit] = useState(false);
  const [domainTypes, setDomainTypes] = useState([]);
  const [groups, setGroups] = useState([]);
  const [showAddTypeDialog, setShowAddTypeDialog] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [newTypeDescription, setNewTypeDescription] = useState('');

  const statusOptions = [
    { value: 'active', label: '🟢 Ativo' },
    { value: 'inactive', label: '🔴 Inativo' },
    { value: 'expired', label: '⏰ Expirado' },
    { value: 'pending', label: '⏳ Pendente' },
    { value: 'maintenance', label: '🟡 Manutenção' }
  ];

  useEffect(() => {
    loadDomainTypes();
    loadGroups();
    if (id) {
      loadDomain(id);
    }
  }, [id]);

  const loadGroups = async () => {
    try {
      const response = await userGroupService.getUserGroups();
      setGroups(response.groups || []);
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
    }
  };

  const loadDomainTypes = async () => {
    try {
      const response = await domainTypeService.getDomainTypes();
      setDomainTypes(response.domain_types || []);
    } catch (error) {
      console.error('Erro ao carregar tipos de domínio:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar tipos de domínio',
        severity: 'error'
      });
    }
  };

  const handleAddDomainType = async () => {
    if (!newTypeName.trim()) {
      setSnackbar({
        open: true,
        message: 'Nome do tipo é obrigatório',
        severity: 'error'
      });
      return;
    }

    try {
      await domainTypeService.createDomainType({
        name: newTypeName.trim(),
        description: newTypeDescription.trim() || null
      });

      setSnackbar({
        open: true,
        message: 'Tipo de domínio adicionado com sucesso!',
        severity: 'success'
      });

      // Recarregar tipos e limpar formulário
      await loadDomainTypes();
      setNewTypeName('');
      setNewTypeDescription('');
      setShowAddTypeDialog(false);

    } catch (error) {
      console.error('Erro ao adicionar tipo de domínio:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Erro ao adicionar tipo de domínio',
        severity: 'error'
      });
    }
  };

  const loadDomain = async (domainId) => {
    try {
      setLoading(true);
      const response = await domainService.getDomain(domainId);
      const domain = response.domain;

      setFormData({
        name: domain.name || '',
        domain_name: domain.domain_name || '',
        domain_type: domain.domain_type || '',
        registration_date: domain.registration_date ? domain.registration_date.split('T')[0] : '',
        expiration_date: domain.expiration_date ? domain.expiration_date.split('T')[0] : '',
        renewal_value: domain.renewal_value ?? '',
        is_active: domain.is_active !== undefined ? domain.is_active : true,
        comments: domain.comments || '',
        status: domain.status || 'active',
        location: domain.location || '',
        technician_in_charge: domain.technician_in_charge || '',
        group_in_charge: domain.group_in_charge || '',
        permission_group_ids: domain.permission_group_ids || (domain.group_id ? [domain.group_id] : [])
      });

      setIsEdit(true);
    } catch (error) {
      console.error('Erro ao carregar domínio:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar domínio',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Processar dados antes de enviar
      const submitData = {
        ...formData
      };

      if (isEdit && id) {
        await domainService.updateDomain(id, submitData);
        setSnackbar({
          open: true,
          message: 'Domínio atualizado com sucesso!',
          severity: 'success'
        });
      } else {
        await domainService.createDomain(submitData);
        setSnackbar({
          open: true,
          message: 'Domínio criado com sucesso!',
          severity: 'success'
        });
      }

      // Redirecionar após sucesso
      setTimeout(() => {
        navigate('/domains');
      }, 1500);

    } catch (error) {
      console.error('Erro no formulário:', error);
      setSnackbar({
        open: true,
        message: error.message || error.data?.error || (isEdit ? 'Erro ao atualizar domínio' : 'Erro ao criar domínio'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Box sx={{
        p: 3,
        maxWidth: '1200px',
        mx: 'auto',
        width: '100%'
      }}>
        {/* Cabeçalho */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
          >
            Voltar
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
              <DomainIcon />
            </Avatar>
            <Box>
              <Typography variant="h4">
                {isEdit ? 'Editar Domínio' : 'Novo Domínio'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isEdit ? 'Edite as informações do domínio' : 'Cadastre um novo domínio web'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Informações Básicas */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <DomainIcon sx={{ mr: 1 }} />
                    Informações Básicas
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Nome do Domínio"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        disabled={loading}
                        placeholder="Ex: Site Institucional, Loja Virtual, API Externa"
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Nome do Domínio"
                        name="domain_name"
                        value={formData.domain_name}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Ex: empresa.com, loja.empresa.com.br"
                        InputProps={{
                          startAdornment: <DomainIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <FormControl fullWidth disabled={loading}>
                          <InputLabel>Tipo de Domínio</InputLabel>
                          <Select
                            name="domain_type"
                            value={formData.domain_type}
                            onChange={handleChange}
                            label="Tipo de Domínio"
                          >
                            {domainTypes.map((type) => (
                              <MenuItem key={type.id} value={type.name}>
                                {type.name}
                                {type.description && (
                                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    - {type.description}
                                  </Typography>
                                )}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Button
                          variant="outlined"
                          onClick={() => setShowAddTypeDialog(true)}
                          disabled={loading}
                          sx={{ minWidth: 'auto', px: 2 }}
                          title="Adicionar novo tipo de domínio"
                        >
                          +
                        </Button>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth disabled={loading}>
                        <InputLabel>Status</InputLabel>
                        <Select
                          name="status"
                          value={formData.status}
                          onChange={handleChange}
                          label="Status"
                        >
                          {statusOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={formData.is_active}
                              onChange={handleSwitchChange}
                              name="is_active"
                              disabled={loading}
                            />
                          }
                          label="Domínio Ativo"
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Datas de Registro e Expiração */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarIcon sx={{ mr: 1 }} />
                    Datas de Registro e Expiração
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Data de Registro"
                        name="registration_date"
                        value={formData.registration_date}
                        onChange={handleChange}
                        disabled={loading}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        InputProps={{
                          startAdornment: <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Data de Expiração"
                        name="expiration_date"
                        value={formData.expiration_date}
                        onChange={handleChange}
                        disabled={loading}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        InputProps={{
                          startAdornment: <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        type="number"
                        label="Valor de Renovação"
                        name="renewal_value"
                        value={formData.renewal_value}
                        onChange={handleChange}
                        disabled={loading}
                        inputProps={{ min: 0, step: '0.01' }}
                        placeholder="Ex: 199.90"
                        InputProps={{
                          startAdornment: <AttachMoneyIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControl fullWidth disabled={loading}>
                        <InputLabel>Grupos de Permissão</InputLabel>
                        <Select
                          name="permission_group_ids"
                          value={formData.permission_group_ids}
                          onChange={handleChange}
                          label="Grupos de Permissão"
                          multiple
                        >
                          {groups.map((group) => (
                            <MenuItem key={group.id} value={group.id}>
                              {group.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Comentários */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <CommentIcon sx={{ mr: 1 }} />
                    Comentários
                  </Typography>

                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Comentários e Observações"
                    name="comments"
                    value={formData.comments}
                    onChange={handleChange}
                    disabled={loading}
                    placeholder="Adicione comentários, propósito do domínio, configurações especiais..."
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Relacionamentos GLPI */}
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <GroupIcon sx={{ mr: 1 }} />
                    Relacionamentos e Responsabilidades
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Localização"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Servidor, datacenter, ambiente..."
                        InputProps={{
                          startAdornment: <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Técnico Responsável"
                        name="technician_in_charge"
                        value={formData.technician_in_charge}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Nome do técnico responsável"
                        InputProps={{
                          startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Grupo Responsável"
                        name="group_in_charge"
                        value={formData.group_in_charge}
                        onChange={handleChange}
                        disabled={loading}
                        placeholder="Nome do grupo/setor responsável"
                        InputProps={{
                          startAdornment: <GroupIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Informações Adicionais */}
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <DomainIcon sx={{ mr: 1 }} />
                    Resumo do Domínio
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Nome:</strong> {formData.name || 'Não definido'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Domínio:</strong> {formData.domain_name ? `${formData.domain_name}${formData.domain_type || ''}` : 'Não definido'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Status:</strong> {formData.is_active ? '🟢 Ativo' : '🔴 Inativo'}
                    </Typography>
                    {formData.registration_date && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Registrado:</strong> {new Date(formData.registration_date).toLocaleDateString('pt-BR')}
                      </Typography>
                    )}
                    {formData.expiration_date && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Expira:</strong> {new Date(formData.expiration_date).toLocaleDateString('pt-BR')}
                      </Typography>
                    )}
                    {formData.renewal_value !== '' && formData.renewal_value !== null && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Valor de renovação:</strong> R$ {Number(formData.renewal_value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                      </Typography>
                    )}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Typography variant="body2" color="text.secondary">
                    💡 Campos obrigatórios: Nome
                    <br />
                    📅 Datas de registro/expiração são opcionais
                    <br />
                    🌐 Domínio deve incluir TLD (.com, .com.br, etc.)
                    <br />
                    🔄 Status ativo/inativo afeta disponibilidade
                  </Typography>
                </CardContent>
              </Card>

              {/* Ações */}
              <Box sx={{ mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  startIcon={<SaveIcon />}
                  disabled={loading}
                >
                  {loading ? 'Salvando...' : 'Salvar Domínio'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>

        {/* Dialog para adicionar novo tipo de domínio */}
        <Dialog
          open={showAddTypeDialog}
          onClose={() => setShowAddTypeDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
            <DomainIcon sx={{ mr: 1 }} />
            Adicionar Novo Tipo de Domínio
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 1 }}>
              <TextField
                fullWidth
                label="Nome do Tipo"
                value={newTypeName}
                onChange={(e) => setNewTypeName(e.target.value)}
                placeholder="Ex: .com, .org, .net.br"
                sx={{ mb: 2 }}
                required
              />
              <TextField
                fullWidth
                label="Descrição (opcional)"
                value={newTypeDescription}
                onChange={(e) => setNewTypeDescription(e.target.value)}
                placeholder="Ex: Domínio comercial genérico"
                multiline
                rows={2}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setShowAddTypeDialog(false);
                setNewTypeName('');
                setNewTypeDescription('');
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleAddDomainType}
              variant="contained"
              disabled={!newTypeName.trim()}
            >
              Adicionar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar para notificações */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
};

export default DomainForm;
